import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box, Button, Container, GlobalStyles, Stack, styled, Typography } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import fabricImageUrl from '@resources/img/pages/404/fabric.svg?url';
import notFoundImageUrl from '@resources/img/pages/404/ghost.svg?url';
import HttpStatus from './Routing/HttpStatus';

const PageShell = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '1 0 auto',
  overflow: 'hidden',
  padding: theme.spacing(6, 0),
  position: 'relative',

  [theme.breakpoints.down('sm')]: {
    minHeight: 'calc(100dvh - 52px)',
    padding: theme.spacing(4, 0),
  },
}));

const FloatingImage = styled('img')(({ theme }) => ({
  animation: 'notFoundFloat 3s ease-in-out infinite',
  display: 'block',
  height: 'auto',
  maxHeight: 320,
  maxWidth: '100%',
  position: 'relative',
  width: 220,
  userSelect: 'none',

  '@keyframes notFoundFloat': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-18px)',
    },
  },

  [theme.breakpoints.down('sm')]: {
    maxHeight: 260,
    width: 180,
  },
}));

const GhostButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  appearance: 'none',
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  justifyContent: 'center',
  padding: 0,
  WebkitTapHighlightColor: 'transparent',
  userSelect: 'none',

  '&&&&:hover, &&&&:active, &&&&:focus': {
    backgroundColor: 'transparent',
  },

  '&&&&:focus': {
    outline: 0,
  },

  '&&&&:focus-visible': {
    borderRadius: theme.shape.borderRadius,
    outline: '2px solid rgba(0, 0, 0, 0.35)',
    outlineOffset: theme.spacing(1),
  },
}));

const FabricImage = styled('img')(({ theme }) => ({
  bottom: theme.spacing(8),
  height: 'auto',
  maxWidth: 400,
  opacity: 0.9,
  position: 'absolute',
  right: theme.spacing(4),
  width: '30%',
  zIndex: 0,

  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const ghostEffectStyles = {
  '@keyframes notFoundTada': {
    '0%': {
      transform: 'scale(1)',
    },
    '10%, 20%': {
      transform: 'scale(0.9) rotate(-3deg)',
    },
    '30%, 50%, 70%, 90%': {
      transform: 'scale(1.1) rotate(3deg)',
    },
    '40%, 60%, 80%': {
      transform: 'scale(1.1) rotate(-3deg)',
    },
    '100%': {
      transform: 'scale(1) rotate(0)',
    },
  },
  body: {
    transition: 'filter 0.25s, background-color 0.25s',
  },
  'body.ghost': {
    backgroundColor: '#111',
    filter: 'invert(1)',
  },
  'body.ghost #ghost-img': {
    animation: 'notFoundTada 500ms linear 1 both',
  },
};

export default function NotFoundPage() {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const ghostFrameRef = useRef<number | undefined>(undefined);
  const ghostTimeoutRef = useRef<number | undefined>(undefined);

  const clearGhostEffect = useCallback(() => {
    document.body.classList.remove('ghost');

    if (ghostFrameRef.current !== undefined) {
      window.cancelAnimationFrame(ghostFrameRef.current);
      ghostFrameRef.current = undefined;
    }

    if (ghostTimeoutRef.current !== undefined) {
      window.clearTimeout(ghostTimeoutRef.current);
      ghostTimeoutRef.current = undefined;
    }
  }, []);

  const triggerGhostEffect = useCallback(() => {
    clearGhostEffect();

    ghostFrameRef.current = window.requestAnimationFrame(() => {
      document.body.classList.add('ghost');
      ghostFrameRef.current = undefined;

      ghostTimeoutRef.current = window.setTimeout(() => {
        document.body.classList.remove('ghost');
        ghostTimeoutRef.current = undefined;
      }, 500);
    });
  }, [clearGhostEffect]);

  useEffect(() => clearGhostEffect, [clearGhostEffect]);

  return <>
    <HttpStatus code={404} />
    <GlobalStyles styles={ghostEffectStyles} />
    <PageShell>
      <Helmet>
        <title>{t('Page not found')}</title>
        <meta name="description" content={t('The page you are looking for was not found.')} />
        <meta name="robots" content="noindex" />
      </Helmet>
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Stack spacing={1}>
            <Typography
              variant="h1"
              sx={{
                color: 'secondary.main',
                fontSize: {
                  xs: '5rem',
                  sm: '6rem',
                },
                fontWeight: 700,
                lineHeight: 0.9,
              }}
            >
              404
            </Typography>
            <Typography variant="h3" sx={{ color: 'text.secondary' }}>
              {t('Uh Oh! This page doesn\'t exist!')}
            </Typography>
          </Stack>
          <GhostButton
            type="button"
            aria-label={t('Play 404 effect')}
            onClick={triggerGhostEffect}
          >
            <FloatingImage id="ghost-img" src={notFoundImageUrl} alt="" aria-hidden="true" draggable="false" />
          </GhostButton>
          <Button
            color="secondary"
            size="large"
            startIcon={<ArrowBackRoundedIcon />}
            variant="outlined"
            onClick={() => {
              void navigate(-1);
            }}
          >
            {t('Go back')}
          </Button>
        </Stack>
      </Container>
      <FabricImage src={fabricImageUrl} alt="" aria-hidden="true" />
    </PageShell>
  </>
}
